import React, { useMemo, useState } from 'react';
import { Participant, Submission, Task } from '../types';
import Avatar from './Avatar';
import { SearchIcon, ChevronRightIcon, FilterIcon, XIcon, CheckIcon, UsersIcon, CheckSquareIcon } from './icons';

interface ParticipantSidebarProps {
    participants: Participant[];
    groups?: any[]; // Using any to match MissionControlView for now
    submissions: Submission[];
    allSubmissions?: Submission[];
    selectedParticipantId: string | null;
    selectedGroupId?: string | null;
    selectedTaskGroupId?: string | null;
    onSelectParticipant: (id: string | null) => void;
    onSelectGroup?: (id: string | null) => void;
    onSelectTaskGroup?: (id: string | null) => void;
    tasks?: Task[]; // Now optional but used for stats
    className?: string; // Allow custom classes (e.g. focused width)
    onClose?: () => void; // For mobile close
}

const ParticipantSidebar: React.FC<ParticipantSidebarProps> = ({
    participants,
    groups = [],
    submissions,
    allSubmissions,
    selectedParticipantId,
    selectedGroupId = null,
    selectedTaskGroupId = null,
    onSelectParticipant,
    onSelectGroup,
    onSelectTaskGroup,
    tasks = [],
    className = "",
    onClose
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [activeTab, setActiveTab] = useState<'users' | 'groups' | 'tasks'>('users');
    const effectiveSubmissions = allSubmissions || submissions;

    console.log('[DEBUG] ParticipantSidebar render:', {
        allSubmissionsLength: allSubmissions?.length,
        submissionsLength: submissions.length,
        selectedParticipant: selectedParticipantId
    });

    // Auto-switch tab if a group, participant or task group is selected
    React.useEffect(() => {
        if (selectedTaskGroupId) setActiveTab('tasks');
        else if (selectedGroupId) setActiveTab('groups');
        else if (selectedParticipantId) setActiveTab('users');
    }, [selectedParticipantId, selectedGroupId, selectedTaskGroupId]);

    // Calculate stats per participant
    const participantStats = useMemo(() => {
        const stats = new Map<string, {
            total: number;
            pending: number;
            lastActive: string;
            completedTasks: number;
            totalTasks: number;
        }>();

        const uniqueTasks = new Set(tasks.map(t => t.id));
        const totalTaskCount = uniqueTasks.size;

        participants.forEach(p => {
            const pSubmissions = effectiveSubmissions.filter(s => s.participantId === p.id);
            // Sort by date desc to get last active
            pSubmissions.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

            // Count completed tasks (submissions with a valid taskId)
            const submittedTaskIds = new Set(pSubmissions.map(s => s.taskId).filter(id => id && uniqueTasks.has(id)));

            stats.set(p.id, {
                total: pSubmissions.length,
                pending: pSubmissions.filter(s => !s.isReviewed).length,
                lastActive: pSubmissions[0]?.timestamp || '',
                completedTasks: submittedTaskIds.size,
                totalTasks: totalTaskCount
            });
        });
        return stats;
    }, [participants, effectiveSubmissions, tasks]);

    const filteredParticipants = useMemo(() => {
        return participants.filter(p =>
            p.name.toLowerCase().includes(searchTerm.toLowerCase())
        ).sort((a, b) => {
            // Sort by pending count desc, then name
            const statsA = participantStats.get(a.id);
            const statsB = participantStats.get(b.id);
            return (statsB?.pending || 0) - (statsA?.pending || 0);
        });
    }, [participants, searchTerm, participantStats]);

    const taskGroups = useMemo(() => {
        const groups = new Map<string, {
            name: string;
            taskIds: string[];
            submissionCount: number;
            pendingCount: number;
        }>();

        tasks.forEach(task => {
            const groupName = task.customGroup || 'Uncategorized';
            if (!groups.has(groupName)) {
                groups.set(groupName, {
                    name: groupName,
                    taskIds: [],
                    submissionCount: 0,
                    pendingCount: 0
                });
            }
            groups.get(groupName)!.taskIds.push(task.id);
        });

        // Calculate counts
        effectiveSubmissions.forEach(s => {
            if (!s.taskId) return;
            const task = tasks.find(t => t.id === s.taskId);
            const groupName = task?.customGroup || 'Uncategorized';
            const group = groups.get(groupName);
            if (group) {
                group.submissionCount++;
                if (!s.isReviewed) group.pendingCount++;
            }
        });

        return Array.from(groups.values()).sort((a, b) => a.name.localeCompare(b.name));
    }, [tasks, effectiveSubmissions]);

    return (
        <div className={`bg-white border-r border-app-border flex flex-col h-full shrink-0 z-20 overflow-hidden ${className}`}>
            {/* Header */}
            <div className="p-4 border-b border-app-border">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="font-bold text-text-ink text-xl tracking-tight">Participants</h2>
                    <div className="flex items-center gap-2">
                        <span className="bg-app-bg text-text-muted text-xs font-bold px-2 py-0.5 rounded-full">
                            {participants.length}
                        </span>

                        {/* Mobile Close Button */}
                        {onClose && (
                            <button
                                onClick={onClose}
                                className="md:hidden p-1 text-text-faint hover:bg-app-bg rounded-full transition-colors"
                            >
                                <XIcon className="w-5 h-5" />
                            </button>
                        )}
                    </div>
                </div>

                {/* Tab Toggle */}
                <div className="flex bg-app-bg/80 p-1 rounded-xl mb-4">
                    <button
                        onClick={() => setActiveTab('users')}
                        className={`flex-1 py-1.5 text-xs font-medium rounded-md transition-all ${activeTab === 'users'
                            ? 'bg-white text-text-ink font-bold shadow-sm ring-1 ring-app-border'
                            : 'text-text-faint hover:text-text-muted'
                            }`}
                    >
                        Users
                    </button>
                    <button
                        onClick={() => setActiveTab('groups')}
                        className={`flex-1 py-1.5 text-xs font-medium rounded-md transition-all ${activeTab === 'groups'
                            ? 'bg-white text-text-ink font-bold shadow-sm ring-1 ring-app-border'
                            : 'text-text-faint hover:text-text-muted'
                            }`}
                    >
                        Groups
                    </button>
                    <button
                        onClick={() => setActiveTab('tasks')}
                        className={`flex-1 py-1.5 text-xs font-medium rounded-md transition-all ${activeTab === 'tasks'
                            ? 'bg-white text-text-ink font-bold shadow-sm ring-1 ring-app-border'
                            : 'text-text-faint hover:text-text-muted'
                            }`}
                    >
                        Tasks
                    </button>
                </div>

                {/* Search */}
                <div className="relative">
                    <SearchIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-text-faint" />
                    <input
                        type="text"
                        placeholder={
                            activeTab === 'users' ? "Find participant..." :
                                activeTab === 'groups' ? "Find group..." :
                                    "Find task group..."
                        }
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 bg-white border border-app-border rounded-lg text-sm focus:outline-none focus:border-action transition-all placeholder:text-text-faint"
                    />
                </div>
            </div>

            {/* List */}
            <div className="flex-1 overflow-y-auto p-2 space-y-1 custom-scrollbar">
                {activeTab === 'users' ? (
                    <>
                        {/* "All Participants" Option */}
                        <button
                            onClick={() => {
                                onSelectParticipant(null);
                                onSelectGroup?.(null);
                                onSelectTaskGroup?.(null);
                                onClose?.(); // Close on mobile
                            }}
                            className={`w-full flex items-center py-2 px-3 rounded-xl transition-all duration-200 text-left group ${selectedParticipantId === null && selectedGroupId === null && selectedTaskGroupId === null
                                ? 'bg-app-bg text-text-ink'
                                : 'text-text-muted hover:bg-app-bg hover:text-text-ink'
                                }`}
                        >
                            <div className={`w-10 h-10 flex-shrink-0 flex items-center justify-center transition-transform duration-200`}>
                                <FilterIcon className={`w-5 h-5 ${selectedParticipantId === null && selectedGroupId === null && selectedTaskGroupId === null ? 'text-text-muted' : 'text-text-faint group-hover:text-text-faint'}`} />
                            </div>
                            <div className="ml-0.5">
                                <p className={`font-bold text-sm ${selectedParticipantId === null && selectedGroupId === null && selectedTaskGroupId === null ? 'text-text-ink' : ''}`}>
                                    All Activity
                                </p>
                                <p className="text-[11px] text-text-muted opacity-80">View global feed</p>
                            </div>
                        </button>

                        <div className="h-px bg-neutral-100 my-2 mx-2" />

                        {filteredParticipants.map(participant => {
                            const stats = participantStats.get(participant.id);
                            const isSelected = selectedParticipantId === participant.id;
                            const completionRate = stats && stats.totalTasks > 0 ? (stats.completedTasks / stats.totalTasks) : 0;
                            const isFullyComplete = completionRate === 1;

                            return (
                                <button
                                    key={participant.id}
                                    onClick={() => {
                                        onSelectParticipant(participant.id);
                                        onClose?.();
                                    }}
                                    className={`w-full flex items-center py-2 px-3 rounded-xl transition-all duration-200 text-left group ${isSelected
                                        ? 'bg-app-bg text-text-ink'
                                        : 'text-text-muted hover:bg-app-bg hover:text-text-ink'
                                        }`}
                                >
                                    <div className="relative shrink-0 w-10 h-10 flex items-center justify-center">
                                        <Avatar
                                            name={participant.name}
                                            avatarUrl={participant.avatarUrl}
                                            id={participant.id}
                                            className={`w-8 h-8 rounded-full object-cover border-2 ${isSelected ? 'border-app-border' : 'border-transparent group-hover:border-app-border/50'}`}
                                        />
                                        {stats && stats.pending > 0 && (
                                            <div className="absolute top-0 right-0 bg-action text-white text-[9px] font-bold min-w-[18px] h-[18px] flex items-center justify-center rounded-full border border-white shadow-sm px-1">
                                                {stats.pending}
                                            </div>
                                        )}
                                        {isFullyComplete && !stats?.pending && (
                                            <div className="absolute bottom-0 right-0 bg-status-ok text-white w-4 h-4 flex items-center justify-center rounded-full border border-white shadow-sm">
                                                <CheckIcon className="w-2.5 h-2.5" />
                                            </div>
                                        )}
                                    </div>

                                    <div className="flex-1 min-w-0 ml-0.5">
                                        <div className="flex items-center justify-between">
                                            <p className={`font-bold text-[13px] truncate ${isSelected ? 'text-text-ink' : 'text-text-ink'}`}>
                                                {participant.name}
                                            </p>
                                            {isSelected && <ChevronRightIcon className="w-3.5 h-3.5 text-text-faint" />}
                                        </div>
                                        <div className="flex items-center justify-between mt-0.5">
                                            <div className="text-[11px] text-text-muted font-medium">
                                                {stats?.completedTasks || 0}/{stats?.totalTasks || 0} tasks
                                            </div>
                                            <div className="w-16 h-1 rounded-full overflow-hidden ml-2" style={{ backgroundColor: '#E8E4DF' }}>
                                                <div
                                                    className="h-full rounded-full transition-all duration-500 ease-out"
                                                    style={{
                                                        width: `${completionRate * 100}%`,
                                                        backgroundColor: isFullyComplete ? '#6E7A5A' : completionRate > 0 ? '#7BBFAF' : 'transparent',
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </button>
                            );
                        })}
                    </>
                ) : activeTab === 'groups' ? (
                    <div className="space-y-1">
                        {groups.filter(g => g.name.toLowerCase().includes(searchTerm.toLowerCase())).map(group => {
                            const isSelected = selectedGroupId === group.id;
                            return (
                                <button
                                    key={group.id}
                                    onClick={() => {
                                        onSelectGroup?.(group.id);
                                        onClose?.();
                                    }}
                                    className={`w-full flex items-center py-2 px-3 rounded-xl transition-all duration-200 text-left group ${isSelected
                                        ? 'bg-app-bg text-text-ink'
                                        : 'text-text-muted hover:bg-app-bg hover:text-text-ink'
                                        }`}
                                >
                                    <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center transition-transform duration-200">
                                        <UsersIcon className={`w-5 h-5 ${isSelected ? 'text-text-muted' : 'text-text-faint group-hover:text-text-faint'}`} />
                                    </div>
                                    <div className="flex-1 min-w-0 ml-0.5">
                                        <p className={`font-bold text-[13px] truncate ${isSelected ? 'text-text-ink' : 'text-text-ink'}`}>{group.name}</p>
                                        <p className="text-[11px] text-text-muted opacity-80">{group.participantIds?.length || 0} participants</p>
                                    </div>
                                    <ChevronRightIcon className={`w-3.5 h-3.5 transition-colors ${isSelected ? 'text-text-faint' : 'text-text-faint/50 group-hover:text-text-faint'}`} />
                                </button>
                            );
                        })}
                        {groups.length === 0 && (
                            <div className="py-12 text-center">
                                <p className="text-text-faint text-sm">No groups found</p>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="space-y-1">
                        {taskGroups.filter(g => g.name.toLowerCase().includes(searchTerm.toLowerCase())).map(group => {
                            const isSelected = selectedTaskGroupId === group.name;
                            return (
                                <button
                                    key={group.name}
                                    onClick={() => {
                                        onSelectTaskGroup?.(group.name);
                                        onClose?.();
                                    }}
                                    className={`w-full flex items-center py-2 px-3 rounded-xl transition-all duration-200 text-left group ${isSelected
                                        ? 'bg-app-bg text-text-ink'
                                        : 'text-text-muted hover:bg-app-bg hover:text-text-ink'
                                        }`}
                                >
                                    <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center transition-transform duration-200">
                                        <CheckSquareIcon className={`w-5 h-5 ${isSelected ? 'text-text-muted' : 'text-text-faint group-hover:text-text-faint'}`} />
                                    </div>
                                    <div className="flex-1 min-w-0 ml-0.5">
                                        <p className={`font-bold text-[13px] truncate ${isSelected ? 'text-text-ink' : 'text-text-ink'}`}>{group.name}</p>
                                        <p className="text-[11px] text-text-muted opacity-80">{group.taskIds.length} tasks • {group.submissionCount} submissions</p>
                                    </div>
                                    {group.pendingCount > 0 && (
                                        <div className="bg-action text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full mr-1.5 min-w-[18px] h-[18px] flex items-center justify-center border border-white shadow-sm">
                                            {group.pendingCount}
                                        </div>
                                    )}
                                    <ChevronRightIcon className={`w-3.5 h-3.5 transition-colors ${isSelected ? 'text-text-faint' : 'text-text-faint/50 group-hover:text-text-faint'}`} />
                                </button>
                            );
                        })}
                        {taskGroups.length === 0 && (
                            <div className="py-12 text-center">
                                <p className="text-text-faint text-sm">No task groups found</p>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ParticipantSidebar;
